<template>
  <div>
    <main-large-dialog
      :is-visible.sync="computedIsVisible"
      :title="$i18n.t('dialog.title.scadaManager')"
      disagree-text="dialog.close"
      :has-agree="false"
      no-paddings-x
      no-paddings-y
      min-body-height="55vh"
      dialog-width="75%"
      header-shadow
    >
      <template #body>
        <v-col v-if="isFetched" cols="12">
          <div class="form-sticky">
            <v-row justify="end" align="center" no-gutters>
              <v-col cols="3" class="pr-5 py-4 search">
                <data-input
                  v-model="search"
                  :data-type="{
                    name: 'text',
                  }"
                  prepend-inner-icon="mdi-magnify"
                  :placeholder="$i18n.t('default.find')"
                  hide-details
                  clearable
                />
              </v-col>
            </v-row>
            <v-row
              justify="center"
              align="center"
              class="pl-2 border-top border-bottom"
              no-gutters
              style="min-height: 46px"
            >
              <v-col v-for="input of inputsForm" :key="input.name">
                <span class="font-weight-medium">
                  {{ $i18n.t(input.translatePath) }}
                </span>
                <span v-if="input.optional" class="text-caption font-weight-thin font-italic">
                  {{ $i18n.t('dialog.body.optional') }}
                </span>
              </v-col>
              <v-col class="d-flex justify-center align-center font-weight-medium options" style="min-height: 46px">
                <span>
                  {{ $i18n.t('dialog.body.options') }}
                </span>
              </v-col>
            </v-row>
            <v-form ref="newMeasurementForm" v-model="isNewMeasurementValid" class="border-bottom">
              <v-row class="pl-2" no-gutters>
                <v-col v-for="input in inputsForm" :key="input.name" class="pt-2">
                  <data-input v-model="newMeasurement[input.name]" v-bind="input" clearable class="px-2" />
                </v-col>
                <v-col v-if="!isSaving" class="d-flex justify-center options">
                  <v-tooltip color="font" bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon
                        v-on="on"
                        :style="`cursor: ${isNewMeasurementValid ? 'pointer' : 'default'}`"
                        :color="isNewMeasurementValid ? 'success' : 'grey'"
                        @click="addMeasurementHandler"
                      >
                        mdi-check
                      </v-icon>
                    </template>
                    <span>
                      {{
                        isNewMeasurementValid
                          ? $i18n.t('dialog.body.addMeasurement')
                          : $i18n.t('dialog.body.toAddMeasurement')
                      }}
                    </span>
                  </v-tooltip>
                </v-col>
                <v-col v-else class="options">
                  <main-loader size="24" width="2" />
                </v-col>
              </v-row>
            </v-form>
          </div>
          <v-form ref="editMeasurementForm" v-model="isEditMeasurementValid">
            <v-row
              v-for="measurement in filtredMeasurements"
              :key="measurement.id"
              style="min-width: 120%"
              class="pl-2 border-bottom"
              no-gutters
            >
              <v-col v-for="input in inputsEdit" :key="input.name" :class="measurement.isEditing ? 'pt-2' : 'py-2'">
                <data-input
                  v-if="measurement.isEditing"
                  v-model="editMeasurement[input.name]"
                  v-bind="input"
                  class="px-2"
                  clearable
                />
                <span v-else>
                  {{
                    measurement[input.name] && measurement[input.name]['verbose_name']
                      ? measurement[input.name]['verbose_name']
                      : measurement[input.name]
                  }}
                </span>
              </v-col>
              <v-col v-if="!measurement.isEditing && !measurement.isSaving" class="py-2 options">
                <v-tooltip color="font" bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" @click="getMeasurementToEdit(measurement)" class="measurement-icons">
                      mdi-pencil
                    </v-icon>
                  </template>
                  <span> {{ $i18n.t('dialog.body.editMeasurement') }} </span>
                </v-tooltip>
                <v-tooltip color="font" bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" @click="openDeletingDialog(measurement.id)" class="measurement-icons">
                      mdi-delete
                    </v-icon>
                  </template>
                  <span> {{ $i18n.t('dialog.body.deleteMeasurement') }} </span>
                </v-tooltip>
              </v-col>
              <v-col v-if="measurement.isEditing && !measurement.isSaving" class="d-flex justify-center options">
                <v-tooltip color="font" bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon
                      v-on="on"
                      :style="`cursor: ${isEditMeasurementValid ? 'pointer' : 'default'}`"
                      class="measurement-icons"
                      :color="isEditMeasurementValid ? 'success' : 'grey'"
                      @click="editMeasurementHandler"
                    >
                      mdi-check
                    </v-icon>
                  </template>
                  <span>
                    {{
                      isEditMeasurementValid
                        ? $i18n.t('dialog.body.saveChangesInMeasurement')
                        : $i18n.t('dialog.body.toEditMeasurement')
                    }}
                  </span>
                </v-tooltip>
                <v-tooltip color="font" bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" class="measurement-icons" color="error" @click="clearChanges(measurement)">
                      mdi-close
                    </v-icon>
                  </template>
                  <span> {{ $i18n.t('dialog.body.deleteChangesInMeasurement') }} </span>
                </v-tooltip>
              </v-col>
              <v-col v-else-if="measurement.isSaving" class="options">
                <main-loader size="24" width="2" />
              </v-col>
            </v-row>
          </v-form>
        </v-col>
        <main-loader class="pt-10" v-else />
      </template>
    </main-large-dialog>
    <main-dialog
      v-if="isDeleteDialogLoaded"
      :is-visible.sync="isDeleteDialogVisible"
      :title="$i18n.t('dialog.title.deleteScadaMeasurement')"
      :body="$i18n.t('dialog.body.deleteScadaMeasurementConfirmation')"
      agree-text="dialog.agreeDelete"
      :loading="isLoading"
      @agree="deleteMeasurementHandler"
    />
  </div>
</template>

<script>
import { get, call } from 'vuex-pathify';
import Vue from 'vue';

import MainLargeDialog from '@/components/MainLargeDialog';
import DataInput from '@/components/DataInput';
import MainLoader from '@/components/MainLoader';

export default {
  name: 'ScadaManagerDialog',
  components: {
    MainLargeDialog,
    DataInput,
    MainLoader,
    MainDialog: () => import('@/components/MainDialog'),
  },
  props: {
    isVisible: {
      type: Boolean,
      required: false,
    },
  },
  data: () => ({
    search: '',
    isFetched: false,
    isLoading: false,
    isSaving: false,
    isNewMeasurementValid: false,
    isEditMeasurementValid: false,
    isEditMeasurement: false,
    isDeleteDialogLoaded: false,
    isDeleteDialogVisible: false,
    newMeasurement: {
      external_name: null,
      verbose_name: null,
      system: null,
      measure_type: null,
      report_period: null,
      measurement_parameter: null,
      measurement_unit: null,
    },
    editMeasurement: {
      external_name: null,
      verbose_name: null,
      system: null,
      measure_type: null,
      report_period: null,
      measurement_parameter: null,
      measurement_unit: null,
    },
    scadaSystems: [],
    measurementParameters: [],
    measurements: [],
    measurementToEdit: null,
    measurementToDelete: null,
  }),
  computed: {
    scadaMeasurements: get('scada/scadaThreeMeasurements'),
    scadaDataSourceName: get('admin/modulesMapping@scada3.measurements_datasource_name'),
    inputsForm() {
      return [
        {
          name: 'external_name',
          translatePath: 'dialog.body.measurementSeriesIdentifier',
          dataType: { name: 'text' },
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        {
          name: 'verbose_name',
          translatePath: 'dialog.body.verboseName',
          dataType: { name: 'text' },
          rules: [
            v => !!v || v == '0' || 'rules.required',
            v => !this.isVerboseNameTaken.includes(v) || 'rules.verboseNameIsTaken',
          ],
        },
        {
          name: 'system',
          translatePath: 'dialog.body.scadaType',
          dataType: { name: 'select' },
          items: Object.keys(this.scadaSystems),
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        {
          name: 'measure_type',
          translatePath: 'dialog.body.measureType',
          dataType: { name: 'select' },
          items: this.getScadaSystemsItems('supported_measures'),
          itemValue: 'name',
          itemText: 'verbose_name',
          rules: [v => !!v || v == '0' || 'rules.required'],
          disabled: !this.newMeasurement.system,
        },
        {
          name: 'report_period',
          translatePath: 'dialog.body.timeInterval',
          dataType: { name: 'select' },
          items: this.getScadaSystemsItems('aggregate_periods'),
          itemValue: 'name',
          itemText: 'verbose_name',
          disabled: !this.newMeasurement.system || this.newMeasurement.measure_type !== 'aggregate',
        },
        {
          name: 'measurement_parameter',
          translatePath: 'dialog.body.parameter',
          dataType: { name: 'select' },
          items: this.measurementParameters,
          itemValue: 'name',
          itemText: 'verbose_name',
          optional: true,
        },
        {
          name: 'measurement_unit',
          translatePath: 'dialog.body.measurementUnit',
          dataType: { name: 'text' },
          optional: true,
        },
      ];
    },
    inputsEdit() {
      return [
        {
          name: 'external_name',
          dataType: { name: 'text' },
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        {
          name: 'verbose_name',
          dataType: { name: 'text' },
          rules: [
            v => !!v || v == '0' || 'rules.required',
            v => !this.isVerboseNameTaken.includes(v) || 'rules.verboseNameIsTaken',
          ],
        },
        {
          name: 'system',
          dataType: { name: 'select' },
          items: Object.keys(this.scadaSystems),
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        {
          name: 'measure_type',
          dataType: { name: 'select' },
          items: this.getScadaSystemsItems('supported_measures'),
          itemValue: 'name',
          itemText: 'verbose_name',
          rules: [v => !!v || v == '0' || 'rules.required'],
          disabled: !this.editMeasurement.system,
        },
        {
          name: 'report_period',
          dataType: { name: 'select' },
          items: this.getScadaSystemsItems('aggregate_periods'),
          itemValue: 'name',
          itemText: 'verbose_name',
          disabled: !this.editMeasurement.system || this.editMeasurement.measure_type !== 'aggregate',
        },
        {
          name: 'measurement_parameter',
          dataType: { name: 'select' },
          items: this.measurementParameters,
          itemValue: 'name',
          itemText: 'verbose_name',
        },
        {
          name: 'measurement_unit',
          dataType: { name: 'text' },
        },
      ];
    },
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
        this.clearForm();
        this.clearChanges();
      },
    },
    filtredMeasurements() {
      return this.search
        ? this.measurements.filter(
            measurement =>
              measurement.external_name.toLowerCase().includes(this.search.toLowerCase()) ||
              measurement.verbose_name.toLowerCase().includes(this.search.toLowerCase())
          )
        : this.measurements;
    },
    isVerboseNameTaken() {
      if (this.isEditMeasurement) {
        return this.measurements
          .filter(measurement => measurement.id !== this.measurementToEdit)
          .map(measurement => measurement.verbose_name);
      } else {
        return this.measurements.map(measurement => measurement.verbose_name);
      }
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    addMeasurement: call('layers/addDatasourceFeature'),
    editionMeasurement: call('layers/editDatasourceFeature'),
    deleteMeasurement: call('layers/deleteDatasourceFeature'),
    getScadaMeasurements: call('scada/getScadaThreeMeasurements'),
    getScadaSystems: call('scada/getScadaThreeSystems'),
    getScadaMeasurementParameters: call('scada/getScadaThreeMeasurementParameters'),
    getScadaSystemsItems(items) {
      if (this.newMeasurement.system) {
        return this.scadaSystems[this.newMeasurement.system][items]
          ? Object.values(this.scadaSystems[this.newMeasurement.system][items])
          : [];
      } else if (this.editMeasurement.system) {
        return this.scadaSystems[this.editMeasurement.system][items]
          ? Object.values(this.scadaSystems[this.editMeasurement.system][items])
          : [];
      } else {
        return [];
      }
    },
    getScadaSystemsVerboseName(system, type, name) {
      return name ? Object.values(this.scadaSystems[system][type]).find(el => el.name == name) : '';
    },
    getParametersVerboseName(name) {
      return name ? this.measurementParameters.find(parameter => parameter.name === name) : '';
    },
    async getMeasurements() {
      this.isFetched = false;
      await this.getScadaMeasurements(this.scadaDataSourceName);
      this.measurements = this.scadaMeasurements.map(feature => {
        return {
          ...feature,
          measure_type: this.getScadaSystemsVerboseName(feature.system, 'supported_measures', feature.measure_type),
          report_period: this.getScadaSystemsVerboseName(feature.system, 'aggregate_periods', feature.report_period),
          measurement_parameter: this.getParametersVerboseName(feature.measurement_parameter),
          isEditing: false,
          isSaving: false,
        };
      });
      this.isFetched = true;
    },
    getMeasurementToEdit(measurement) {
      if (this.isEditMeasurement) {
        const previousMeasurement = this.measurements.find(measurement => measurement.id === this.measurementToEdit);
        this.clearChanges(previousMeasurement);
      }
      this.isEditMeasurement = true;
      this.measurementToEdit = measurement.id;
      Vue.set(measurement, 'isEditing', true);
      this.editMeasurement = {
        external_name: measurement.external_name,
        verbose_name: measurement.verbose_name,
        system: measurement.system,
        measure_type: measurement.measure_type.name,
        report_period: measurement.report_period.name,
        measurement_parameter: measurement.measurement_parameter.name,
        measurement_unit: measurement.measurement_unit,
      };
    },
    openDeletingDialog(id) {
      this.measurementToDelete = id;
      this.isDeleteDialogVisible = true;
      this.isDeleteDialogLoaded = true;
    },
    clearForm() {
      this.$refs?.newMeasurementForm?.resetValidation();
      this.search = '';
      this.newMeasurement = {
        external_name: null,
        verbose_name: null,
        system: null,
        measure_type: null,
        report_period: null,
        measurement_parameter: null,
        measurement_unit: null,
      };
    },
    clearChanges(measurement) {
      if (measurement) {
        Vue.set(measurement, 'isEditing', false);
      } else if (this.measurementToEdit) {
        const previousMeasurement = this.measurements.find(measurement => measurement.id === this.measurementToEdit);
        this.clearChanges(previousMeasurement);
      }
      this.$refs?.editMeasurementForm?.resetValidation();
      this.isEditMeasurement = false;
      this.editMeasurement = {
        external_name: null,
        verbose_name: null,
        system: null,
        measure_type: null,
        report_period: null,
        measurement_parameter: null,
        measurement_unit: null,
      };
    },
    async addMeasurementHandler() {
      if (!this.isNewMeasurementValid) {
        return;
      }
      if (this.newMeasurement.measure_type !== 'aggregate') {
        this.newMeasurement.report_period = null;
      }
      const payload = {
        datasource: this.scadaDataSourceName,
        body: this.newMeasurement,
      };
      try {
        this.isSaving = true;
        await this.addMeasurement(payload);
        await this.getMeasurements();
        this.clearForm();
      } catch {
        //
      } finally {
        this.isSaving = false;
      }
    },
    async editMeasurementHandler() {
      if (this.isEditMeasurement && !this.isEditMeasurementValid) {
        return;
      }
      if (this.editMeasurement.measure_type !== 'aggregate') {
        this.editMeasurement.report_period = null;
      }
      const editedMeasurement = this.measurements.find(measurement => measurement.id === this.measurementToEdit);
      const payload = {
        datasource: this.scadaDataSourceName,
        body: this.editMeasurement,
        id: this.measurementToEdit,
      };
      try {
        Vue.set(editedMeasurement, 'isSaving', true);
        await this.editionMeasurement(payload);
        await this.getMeasurements();
        this.isEditMeasurement = false;
        this.measurementToEdit = null;
        this.clearChanges();
      } catch {
        //
      } finally {
        Vue.set(editedMeasurement, 'isSaving', false);
      }
    },
    async deleteMeasurementHandler() {
      const payload = {
        datasource: this.scadaDataSourceName,
        id: this.measurementToDelete,
      };
      try {
        this.isLoading = true;
        await this.deleteMeasurement(payload);
        await this.getMeasurements();
      } catch {
        //
      } finally {
        this.isLoading = false;
        this.isDeleteDialogVisible = false;
        this.isDeleteDialogLoaded = false;
        this.measurementToDelete = null;
      }
    },
    async init() {
      try {
        this.isFetched = false;
        const r = await Promise.all([this.getScadaMeasurementParameters(), this.getScadaSystems()]);
        this.measurementParameters = r[0];
        this.scadaSystems = r[1];
        await this.getMeasurements();
      } catch {
        //
      } finally {
        this.isFetched = true;
      }
    },
  },
};
</script>

<style lang="scss">
.search {
  position: sticky;
  right: 0;
  z-index: 1;
}
.options {
  position: sticky;
  right: 0;
  z-index: 1;
  max-width: 80px !important;
  background-color: rgba(#fff, 1);
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    @include border(left, 1px);
  }
}
.form-sticky {
  position: sticky;
  top: 0;
  z-index: 2;
  min-width: 120%;
  background-color: rgba(#fff, 1);
}
.measurement-icons:hover {
  color: black;
}
.v-text-field__details {
  margin-bottom: 0 !important;
}
.border-top {
  @include border(top, 1px);
}
.border-bottom {
  @include border(bottom, 1px);
}
</style>
